import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Seo from '../components/common/Seo';
import PageHero from '../components/common/PageHero';
import { company } from '@/data/company';
import { seo } from '@/data/seo';

const About = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      <Seo {...seo.about} />
      <PageHero
        eyebrow="About MAYAN Technology"
        title="Engineering excellence"
        highlight={`since ${company.foundedYear}`}
        description={company.about[0]}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-8"
        >
          {company.highlights.map((item) => (
            <div key={item.label} className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-[#398024] mb-2">{item.value}</div>
              <div className="text-gray-600 text-sm font-medium">{item.label}</div>
            </div>
          ))}
        </motion.div>
      </PageHero>

      <section className="py-20 relative bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-sm font-semibold text-[#398024] tracking-wider uppercase mb-3">Our story</h2>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">A Jordanian engineering establishment</h3>
              {company.about.slice(1).map((paragraph) => (
                <p key={paragraph} className="text-gray-600 leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="relative">
              <img
                src="/assets/content/about.png"
                alt="MAYAN Technology engineering and industrial work"
                width={800}
                height={600}
                className="w-full rounded-3xl border border-gray-200 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 relative bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="w-12 h-12 bg-[#398024] rounded-xl flex items-center justify-center mb-6">
                <Target className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed">{company.mission}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="w-12 h-12 bg-[#398024] rounded-xl flex items-center justify-center mb-6">
                <Eye className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
              <p className="text-gray-600 leading-relaxed">{company.vision}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 relative bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-sm font-semibold text-[#398024] tracking-wider uppercase mb-3">Capabilities</h2>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">{company.electromechanical.title}</h3>
              <p className="text-gray-600 leading-relaxed mb-8">{company.electromechanical.description}</p>
              <ul className="space-y-3">
                {company.electromechanical.items.map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="text-[#398024] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <img
              src="/assets/content/industrial.jpg"
              alt="Industrial plant and process equipment"
              width={1200}
              height={800}
              className="w-full rounded-3xl border border-gray-200 object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="py-20 relative bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Ready to work with MAYAN?</h3>
          <p className="text-xl text-gray-600 mb-8">
            Discuss engineering, industrial, or technology requirements with the team in Amman.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#398024] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#2d6b1a] hover:shadow-xl transition-all duration-300"
          >
            Get in touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
